/**
 * Toegang tot het beeld- en geluidsmateriaal.
 *
 * Het manifest wordt gegenereerd door `tools/build-assets.ts` uit het archief van de
 * cd. Namen zijn hier logisch ("landschappen/bergen"), niet fysiek — het bestandspad
 * bevat een content-hash en verandert zodra de bron verandert.
 */
import manifest from './asset-manifest.json' with { type: 'json' };
import { UI } from './klanken.js';

export interface AssetEntry {
  readonly src: string;
  readonly width: number;
  readonly height: number;
}

type Manifest = Record<string, Record<string, AssetEntry>>;
const data = manifest as Manifest;

/** Zoekt een asset op. Ontbreekt hij, dan is dat een fout in de pijplijn, geen geval om stil op te vangen. */
export function asset(groep: string, naam: string): AssetEntry {
  const entry = data[groep]?.[naam];
  if (entry === undefined) {
    throw new Error(`asset ontbreekt: ${groep}/${naam} — draai "pnpm assets"`);
  }
  return entry;
}

/** Bestaat deze asset? Voor gevallen waar een terugval acceptabel is. */
export function hasAsset(groep: string, naam: string): boolean {
  return data[groep]?.[naam] !== undefined;
}

export function assetGroup(groep: string): Record<string, AssetEntry> {
  return data[groep] ?? {};
}

/**
 * Alles wat het scherm meteen nodig heeft.
 *
 * Pixi's `Texture.from()` op een nog niet geladen adres levert een leeg beeld op — je
 * ziet dan wel de teksten maar geen omlijsting. Daarom laden we de skin vooraf; de
 * kaartafbeeldingen mogen wachten tot ze in beeld komen.
 */
export function boardImages(): string[] {
  const groepen = [
    'landschappen',
    'bouwwerken',
    'symbolen',
    'symbolen-groot',
    'dobbelstenen',
    'achtergrond',
    'rivier',
    'decoratie',
    'skin',
    'portretten',
    'banners',
    'menubalken',
    // De kaartplaten horen erbij: zonder vooraf laden blijft een uitgeklapte hand leeg.
    'kaarten',
  ];
  // En alle animatiereeksen. Zonder die hier neer te zetten zag je van elke animatie
  // alleen het eindbeeld: de dobbelsteen sprong meteen op zijn vlak, het schaap vloog
  // niet weg en de flits bleef uit. De reeksen zijn klein (45×45 tot 100×100) maar
  // talrijk, en `Texture.from()` op iets wat nog niet geladen is levert leegte op.
  const animaties = Object.keys(data).filter((g) => g.startsWith('anim-'));
  return [...groepen, ...animaties].flatMap((g) =>
    Object.values(assetGroup(g)).map((e) => e.src),
  );
}

// ---------------------------------------------------------------- geluid

const audioCache = new Map<string, HTMLAudioElement>();
/** De lopende vervaging per element, zodat hergebruik hem kan afzeggen. */
const fades = new WeakMap<HTMLAudioElement, number>();
let geluidAan = true;

export function setSoundEnabled(aan: boolean): void {
  geluidAan = aan;
}

export function isSoundEnabled(): boolean {
  return geluidAan;
}

/**
 * Het ene element per geluidsbestand, klaargezet om (opnieuw) te spelen.
 *
 * Hergebruik is hier geen zuinigheid maar de iPad-sleutel: iOS ontgrendelt geluid per
 * elemént zodra het één keer rond een aanraking heeft gespeeld; een vers `new Audio()`
 * begint weer op slot. De verhaalbaan maakte per stuk een nieuw element en zou op een
 * iPad dus telkens opnieuw geweigerd kunnen worden.
 */
function gebruikElement(src: string): HTMLAudioElement {
  let audio = audioCache.get(src);
  if (audio === undefined) {
    audio = new Audio(src);
    audio.preload = 'auto';
    audioCache.set(src, audio);
  }
  // Een eerdere vervaging afzeggen en het volume terugzetten: een gedeeld element dat
  // op nul bleef staan is een klank die nooit meer klinkt.
  const fade = fades.get(audio);
  if (fade !== undefined) {
    window.clearInterval(fade);
    fades.delete(audio);
  }
  audio.volume = 1;
  audio.currentTime = 0;
  return audio;
}

/**
 * Speelt een geluid af. Ontbrekende geluiden worden stil overgeslagen: het spel mag
 * nooit stuklopen omdat er een klankje mist.
 */
export function playSound(groep: string, naam: string): void {
  if (!geluidAan) return;
  const entry = data[`geluid-${groep}`]?.[naam];
  if (entry === undefined) return;

  void gebruikElement(entry.src).play().catch(() => {
    // Browsers weigeren geluid voordat de gebruiker iets heeft aangeklikt; dat is geen fout.
  });
}

/**
 * Laat een lopend geluid wegvagen in plaats van het af te kappen.
 *
 * Een muziekstuk dat midden in een maat ophoudt hoor je als een fout. Twaalf stapjes over
 * een halve seconde is genoeg om er een einde van te maken in plaats van een knip.
 */
function vervaagGeluid(audio: HTMLAudioElement, ms: number): void {
  if (ms <= 0 || audio.paused) {
    audio.pause();
    return;
  }
  const stappen = 12;
  const begin = audio.volume;
  let stap = 0;
  const tik = window.setInterval(() => {
    stap += 1;
    audio.volume = Math.max(0, begin * (1 - stap / stappen));
    if (stap >= stappen) {
      window.clearInterval(tik);
      fades.delete(audio);
      audio.pause();
      audio.currentTime = 0;
    }
  }, ms / stappen);
  // Geregistreerd, zodat `gebruikElement` een lopende vervaging kan afzeggen als
  // hetzelfde stuk opnieuw begint — anders pauzeert de oude wekker het nieuwe spel.
  fades.set(audio, tik);
}

/**
 * Speelt een geluid en geeft je de handgreep om het weer weg te laten vagen.
 *
 * De handgreep neemt een fadeduur aan; nul betekent meteen stil. Hij is er niet om het
 * stuk kort te houden — dat deed hij vroeger wel, en dan werd Conflict na vier seconden
 * afgekapt terwijl het twaalf en een halve duurt — maar om plaats te maken als er iets
 * anders begint.
 */
export function speelEnStop(
  groep: string,
  naam: string,
): ((fadeMs?: number) => void) | undefined {
  if (!geluidAan) return undefined;
  const entry = data[`geluid-${groep}`]?.[naam];
  if (entry === undefined) return undefined;
  const audio = gebruikElement(entry.src);
  void audio.play().catch(() => {
    // Browsers weigeren geluid voor de eerste klik; dat is geen fout.
  });
  return (fadeMs = 0) => vervaagGeluid(audio, fadeMs);
}

/**
 * De verhaalbaan: het stuk dat hoort bij wat er nú op het scherm gebeurt.
 *
 * Kaartmuziek, gebeurtenismuziek en het toernooistuk delen deze ene baan. Ze duren van
 * vier tot tweeëntwintig seconden en mogen uitspelen — het origineel kapt ze ook niet af
 * zodra de kaart weg is. Maar twee stukken tegelijk is geen sfeer, dat is herrie: een
 * nieuw stuk laat het vorige eerst wegvagen.
 */
let verhaal: ((fadeMs?: number) => void) | undefined;

export function speelVerhaal(groep: string, naam: string): void {
  stopVerhaal();
  verhaal = speelEnStop(groep, naam);
}

/** Maakt de baan vrij. Zonder argument met een korte fade. */
export function stopVerhaal(fadeMs = 600): void {
  verhaal?.(fadeMs);
  verhaal = undefined;
}

/** Een willekeurig geluid uit een groep — voor variatie bij herhaalde handelingen. */
export function playRandomSound(groep: string): void {
  const namen = Object.keys(data[`geluid-${groep}`] ?? {});
  if (namen.length === 0) return;
  playSound(groep, namen[Math.floor(Math.random() * namen.length)]!);
}

/**
 * Het klikje van de cd-versie.
 *
 * In het origineel geeft elke klik hetzelfde korte tikje. Dat hoort op één plek te
 * staan, anders is het ergens vergeten — en juist dat valt op.
 */
export function klik(): void {
  playSound('interface', UI.klik);
}
